import React, { useState } from "react";
import axios from "axios";
import { useI18n } from "@/i18n/I18nContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { Send, Sparkles, BarChart3, Globe2 } from "lucide-react";

export default function AdvertisePage() {
  const { t } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", business_name: "", business_type: "hotel", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success(t("form_success"));
      setForm({ name: "", email: "", business_name: "", business_type: "hotel", message: "" });
    } catch (err) {
      toast.error("Could not send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen" data-testid="advertise-page">
      <section className="bg-[#0A2540] text-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 pt-28 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.3em] font-bold text-[var(--brand-accent)] mb-4">For Local Businesses</p>
            <h1 className="font-display text-5xl sm:text-7xl tracking-tight leading-[0.95]">{t("advertise_title")}</h1>
            <p className="mt-6 text-stone-300 text-lg max-w-xl">{t("advertise_subtitle")}</p>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
                <Globe2 className="h-5 w-5 text-[var(--brand-accent)]" />
                <p className="font-display text-xl mt-2">9 Languages</p>
                <p className="text-sm text-stone-300 mt-1">Reach travellers worldwide.</p>
              </div>
              <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
                <Sparkles className="h-5 w-5 text-[var(--brand-accent)]" />
                <p className="font-display text-xl mt-2">Featured Cards</p>
                <p className="text-sm text-stone-300 mt-1">Premium glow & ranking.</p>
              </div>
              <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
                <BarChart3 className="h-5 w-5 text-[var(--brand-accent)]" />
                <p className="font-display text-xl mt-2">Targeted Reach</p>
                <p className="text-sm text-stone-300 mt-1">Travellers actually planning a trip.</p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5">
            <form onSubmit={submit} className="rounded-3xl bg-white text-stone-900 p-7 shadow-xl space-y-4" data-testid="advertise-form">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-stone-500">{t("form_name")}</label>
                <Input required value={form.name} onChange={(e) => onChange("name", e.target.value)} className="mt-1.5" data-testid="adv-name" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-stone-500">{t("form_email")}</label>
                <Input type="email" required value={form.email} onChange={(e) => onChange("email", e.target.value)} className="mt-1.5" data-testid="adv-email" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-stone-500">{t("form_business")}</label>
                  <Input value={form.business_name} onChange={(e) => onChange("business_name", e.target.value)} className="mt-1.5" data-testid="adv-business" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-stone-500">{t("form_business_type")}</label>
                  <Select value={form.business_type} onValueChange={(v) => onChange("business_type", v)}>
                    <SelectTrigger className="mt-1.5" data-testid="adv-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hotel">Hotel</SelectItem>
                      <SelectItem value="restaurant">Restaurant</SelectItem>
                      <SelectItem value="diving">Diving</SelectItem>
                      <SelectItem value="shop">Shop</SelectItem>
                      <SelectItem value="activity">Activity</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-stone-500">{t("form_message")}</label>
                <Textarea required rows={5} value={form.message} onChange={(e) => onChange("message", e.target.value)} className="mt-1.5" data-testid="adv-message" />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white rounded-full h-11"
                data-testid="adv-submit"
              >
                {submitting ? "Sending…" : <span className="inline-flex items-center gap-2">{t("form_submit")} <Send className="h-4 w-4" /></span>}
              </Button>
            </form>
          </div>
        </div>
      </section>
    </main>
  );
}
